import React, { useMemo } from "react";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { IFolder } from "store/types";

import { useClientWorkspaceCtx } from "../context/ClientWorkspace-context";

const buildAncestorPath = (folders: IFolder[], selectedFolder: IFolder): IFolder[] => {
  const path: IFolder[] = [];
  let current = selectedFolder;

  while (current.parent_id !== null) {
    const parent = folders.find((f) => f.id === current.parent_id);
    if (!parent) break;
    path.unshift(parent);
    current = parent;
  }

  return path;
};

const crumbTextSx = {
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "16px",
  color: "#496682",
  whiteSpace: "nowrap" as const,
  cursor: "pointer",
  "&:hover": { color: "#071f35" }
};

const folderCrumbTextSx = {
  ...crumbTextSx,
  maxWidth: 160,
  overflow: "hidden",
  textOverflow: "ellipsis"
};

interface Props {
  showCurrentFolder?: boolean;
}

export const BreadCrumbs: React.FC<Props> = ({ showCurrentFolder = false }) => {
  const { folders, currentFolder, onWorkspaceFolderClick, onFolderClick } = useClientWorkspaceCtx();

  const path = useMemo(() => {
    if (!currentFolder) return [];
    const path = buildAncestorPath(folders, currentFolder);
    if (showCurrentFolder) path.push(currentFolder);
    return path;
  }, [folders, currentFolder]);

  return (
    <Stack direction="row" alignItems="center" sx={{ minWidth: 0, overflow: "hidden" }}>
      <Stack
        direction="row"
        alignItems="center"
        onClick={onWorkspaceFolderClick}
        sx={{ cursor: "pointer", flexShrink: 0 }}
      >
        <Typography sx={crumbTextSx}>Workspace</Typography>
        <ChevronRightIcon sx={{ fontSize: 24, color: "#496682", flexShrink: 0 }} />
      </Stack>

      {path.map((folder, index, array) => (
        <Stack
          key={folder.id}
          direction="row"
          alignItems="center"
          onClick={() => onFolderClick(folder.id)}
          sx={{ cursor: "pointer", minWidth: 0 }}
        >
          <MaterialTooltip title={folder.title} placement="bottom">
            <Typography sx={folderCrumbTextSx}>{folder.title}</Typography>
          </MaterialTooltip>
          {index < array.length - 1 && <ChevronRightIcon sx={{ fontSize: 24, color: "#496682", flexShrink: 0 }} />}
        </Stack>
      ))}
    </Stack>
  );
};
